import { useState, useEffect, useRef } from 'react';
import styles from './ProducerCompanyPage.module.css';
import { useAuth } from '../../../contexts/AuthContext';
import ProducerContentContainer from '../layout/ProducerContentContainer';
import {
  getMyProducerProfile,
  getProducerWorks,
  createProducerWork,
  updateProducerWork,
  deleteProducerWork,
  getProducerReviews,
  updateProducerProfile,
  deleteProducerLogo,
  patchProducerCompanyProfile,
  getProducerStats,
} from '../../../api/producerApi';
import ComingSoonBox from '../../../shared/messages/ComingSoonBox';
import { getMyCastingList } from '../../../api/castingApi';
import { uploadFile } from '../../../api/fileApi';
import { PRODUCER_PLACEHOLDER, buildProfilePlaceholderDataUrl } from '../../../shared/utils/profilePlaceholders';

const TABS = ['회사 소개', '제작 작품', '진행 중인 공고'];
const GENRES = ['영화', '드라마', '예능', '다큐멘터리', '유튜브', '숏폼', '광고', '뮤직비디오', '브랜드 콘텐츠'];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1949 }, (_, i) => CURRENT_YEAR - i);

function getSafeImageUrl(file) {
  if (!file) return null;
  return URL.createObjectURL(file);
}

function syncProducerIdentity(setUserInfo, nextProfile = {}) {
  const companyName = nextProfile.companyName || nextProfile.name || '';
  const profileImage =
    nextProfile.logoUrl ||
    nextProfile.profileImage ||
    nextProfile.imageUrl ||
    PRODUCER_PLACEHOLDER;

  setUserInfo?.({
    name: companyName,
    profileImage,
  });
}

const PRODUCER_LOGO_REMOVED_KEY = 'producer-logo-removed';

export function isProducerLogoRemoved() {
  try { return localStorage.getItem(PRODUCER_LOGO_REMOVED_KEY) === 'true'; } catch { return false; }
}

function markProducerLogoRemoved(removed) {
  try { localStorage.setItem(PRODUCER_LOGO_REMOVED_KEY, removed ? 'true' : 'false'); } catch {}
}

function resolveProducerLogoUrl(profile = {}) {
  if (isProducerLogoRemoved()) return null;
  return profile.logoUrl || profile.profileImage || profile.imageUrl || null;
}

function persistProducerIdentity(nextProfile = {}) {
  const companyName = nextProfile.companyName || nextProfile.name || '';
  const logoUrl = resolveProducerLogoUrl(nextProfile);
  const profileImage = logoUrl || buildProfilePlaceholderDataUrl(companyName, 'company');

  localStorage.setItem('producerIdentityOverride', JSON.stringify({
    name: companyName,
    profileImage: logoUrl ? profileImage : null, // null이면 다음 로드 시 placeholder 사용
  }));
}

function dday(dateStr) {
  if (!dateStr) return '';
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return '마감';
  if (diff === 0) return 'D-Day';
  return `D-${diff}`;
}

function GenreTag({ label, yellow }) {
  return (
    <span className={`${styles.genreTag} ${yellow ? styles.genreTagYellow : styles.genreTagGray}`}>
      {label}
    </span>
  );
}

function parseSpecialties(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string' && raw.trim()) return raw.split(',').map(s => s.trim()).filter(Boolean);
  return [];
}

function ContactIcon({ type }) {
  const base = { width: 20, height: 20, flexShrink: 0, style: { display: 'block' } };

  if (type === 'email') {
    return (
      <svg {...base} viewBox="0 0 24 24" fill="none">
        <rect x="2" y="4" width="20" height="16" rx="2" stroke="#A2A2A2" strokeWidth="1.5" />
        <path d="M2 8l10 7 10-7" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'phone') {
    return (
      <svg {...base} viewBox="0 0 24 24" fill="none">
        <path
          d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"
          stroke="#A2A2A2"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === 'web') {
    return (
      <svg {...base} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="#A2A2A2" strokeWidth="1.5" />
        <path
          d="M12 3c-2.5 3-4 5.7-4 9s1.5 6 4 9M12 3c2.5 3 4 5.7 4 9s-1.5 6-4 9M3 12h18"
          stroke="#A2A2A2"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === 'sns') {
    return (
      <svg {...base} viewBox="0 0 24 24" fill="none">
        <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" stroke="#A2A2A2" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="4.2" stroke="#A2A2A2" strokeWidth="1.8" />
        <circle cx="17.2" cy="6.8" r="1.5" fill="#A2A2A2" />
      </svg>
    );
  }

  return null;
}

function MetaItem({ icon, label }) {
  const iconEl =
    icon === 'calendar' ? (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="#A2A2A2" strokeWidth="1.5" />
        <path d="M16 2v4M8 2v4M3 10h18" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ) : icon === 'location' ? (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
          fill="#A2A2A2"
        />
      </svg>
    ) : (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <circle cx="9" cy="7" r="3" stroke="#A2A2A2" strokeWidth="1.5" />
        <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M16 3.13a4 4 0 010 7.75M21 20c0-2.76-2.24-5-5-5" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );

  return (
    <div className={styles.companyMetaItem}>
      {iconEl}
      <span className={styles.companyMetaLabel}>{label}</span>
    </div>
  );
}

function CompanyOverview({ profile, onProfileUpdate }) {
  const { setUserInfo } = useAuth();
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    companyName: '',
    description: '',
    foundedYear: '',
    address: '',
    employeeCount: '',
    specialties: [],
    website: '',
    contactEmail: '',
    phone: '',
    instagramHandle: '',
    logoUrl: null,
    logoFileId: null,
  });
  const [logoFile, setLogoFile] = useState(null);
  const logoInputRef = useRef(null);
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const tagInputRef = useRef(null);
  const isEditRef = useRef(false);
  const profileRef = useRef(profile);

  useEffect(() => {
    isEditRef.current = isEdit;
  }, [isEdit]);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => () => {
    if (isEditRef.current) {
      syncProducerIdentity(setUserInfo, profileRef.current);
    }
  }, [setUserInfo]);

  const set = (key) => (e) => setEditData((p) => ({ ...p, [key]: e.target.value }));

  const handleEnterEdit = () => {
    setEditData({
      companyName: profile.companyName || '',
      description: profile.description || '',
      foundedYear: profile.foundedYear ? String(profile.foundedYear) : '',
      address: profile.address || '',
      employeeCount: profile.employeeCount || '',
      specialties: parseSpecialties(profile.specialties),
      website: profile.website || '',
      contactEmail: profile.contactEmail || '',
      phone: profile.phone || '',
      instagramHandle: profile.instagramHandle || '',
      logoUrl: profile.logoUrl || null,
      logoFileId: null,
    });
    setLogoFile(null);
    setIsEdit(true);
  };

  // 로고를 클릭했을 때 편집 모드로 전환하고 바로 파일 선택창을 띄움
  const handleLogoClickFromReadOnly = () => {
    handleEnterEdit();
    // DOM이 편집 모드로 렌더링되어 input이 생성될 때까지 대기 후 클릭 트리거
    setTimeout(() => {
      logoInputRef.current?.click();
    }, 100);
  };

  const handleCancel = () => {
    syncProducerIdentity(setUserInfo, profile);
    setIsEdit(false);
    setLogoFile(null);
    setTagInput('');
    setShowTagInput(false);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    const previewUrl = getSafeImageUrl(file);
    setEditData((p) => ({ ...p, logoUrl: previewUrl }));
    // 저장 전까지는 헤더/사이드바 업데이트 안 함
  };

  const handleDeleteLogo = (e) => {
    e.stopPropagation();
    setLogoFile(null);
    setEditData((p) => ({ ...p, logoUrl: null, logoFileId: null }));
    if (logoInputRef.current) logoInputRef.current.value = '';
    // 저장 전까지는 헤더/사이드바 업데이트 안 함
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let logoFileId = editData.logoFileId;
      let newLogoUrl = editData.logoUrl;
      const isDeletingLogo = editData.logoUrl === null;

      if (isDeletingLogo) {
        logoFileId = null;
        newLogoUrl = null;
      } else if (logoFile) {
        const result = await uploadFile(logoFile, 'IMAGE');
        logoFileId = result.fileEntityId;
        newLogoUrl = result.url;
      }

      const optimisticProfile = {
        ...profile,
        companyName: editData.companyName,
        description: editData.description,
        foundedYear: editData.foundedYear,
        address: editData.address,
        employeeCount: editData.employeeCount,
        specialties: editData.specialties,
        website: editData.website,
        contactEmail: editData.contactEmail,
        phone: editData.phone,
        instagramHandle: editData.instagramHandle,
        logoUrl: newLogoUrl ?? null,
      };

      onProfileUpdate(optimisticProfile);
      syncProducerIdentity(setUserInfo, optimisticProfile);
      persistProducerIdentity(optimisticProfile);

      if (isDeletingLogo) {
        try {
          await deleteProducerLogo();
        } catch (error) {
          const status = error?.response?.status;
          if (status !== 404 && status !== 405) throw error;
          await updateProducerProfile({
            id: profile.id,
            companyName: editData.companyName,
            phone: editData.phone,
            contactEmail: editData.contactEmail,
            website: editData.website,
            address: editData.address,
            managerName: profile.managerName || '',
            businessRegNumber: profile.businessRegNumber || '',
            logoFileId: null,
            logoUrl: null,
          });
        }
      } else {
        await updateProducerProfile({
          id: profile.id,
          companyName: editData.companyName,
          phone: editData.phone,
          contactEmail: editData.contactEmail,
          website: editData.website,
          address: editData.address,
          managerName: profile.managerName || '',
          businessRegNumber: profile.businessRegNumber || '',
          logoFileId,
        });
      }

      try {
        await patchProducerCompanyProfile({
          companyDescription: editData.description,
          specialties: editData.specialties,
          foundedYear: editData.foundedYear ? Number(editData.foundedYear) : null,
          employeeCount: editData.employeeCount || null,
          instagramHandle: editData.instagramHandle || null,
        });
      } catch (_) {}

      let latestProfile = optimisticProfile;
      try {
        const refreshed = await getMyProducerProfile();
        if (refreshed) latestProfile = refreshed;
      } catch (_) {}

      const serverLogoUrl = resolveProducerLogoUrl(latestProfile);
      const logoDeletePersisted = !isDeletingLogo || !serverLogoUrl;
      markProducerLogoRemoved(logoDeletePersisted ? isDeletingLogo : false);

      latestProfile = {
        ...latestProfile,
        companyName: editData.companyName,
        description: editData.description,
        foundedYear: editData.foundedYear,
        address: editData.address,
        employeeCount: editData.employeeCount,
        specialties: editData.specialties,
        website: editData.website,
        contactEmail: editData.contactEmail,
        phone: editData.phone,
        instagramHandle: editData.instagramHandle,
        logoUrl: logoDeletePersisted ? (newLogoUrl ?? null) : serverLogoUrl,
      };

      onProfileUpdate(latestProfile);
      syncProducerIdentity(setUserInfo, latestProfile);
      persistProducerIdentity(latestProfile);

      if (isDeletingLogo && !logoDeletePersisted) {
        alert('회사 로고 삭제가 서버에 반영되지 않았습니다. 현재 API에서 로고 삭제를 지원하는지 백엔드 확인이 필요합니다.');
      }
    } catch (_) {
      markProducerLogoRemoved(false);
      syncProducerIdentity(setUserInfo, profile);
      persistProducerIdentity(profile);
      alert('회사 정보 저장에 실패했습니다.');
    } finally {
      setSaving(false);
      setIsEdit(false);
      setLogoFile(null);
      setTagInput('');
      setShowTagInput(false);
    }
  };

  const commitTag = (val) => {
    const v = val.trim();
    if (v && !editData.specialties.includes(v)) {
      setEditData((p) => ({ ...p, specialties: [...p.specialties, v] }));
    }
  };

  const removeTag = (tag) =>
    setEditData((p) => ({ ...p, specialties: p.specialties.filter((t) => t !== tag) }));

  const handleTagKey = (e) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      commitTag(tagInput);
      setTagInput('');
    }
    if (e.key === 'Escape') {
      setShowTagInput(false);
      setTagInput('');
    }
  };

  const handleTagBlur = () => {
    if (tagInput.trim()) {
      commitTag(tagInput);
      setTagInput('');
    }
    setShowTagInput(false);
  };

  const handlePlusClick = () => {
    setShowTagInput(true);
    setTimeout(() => tagInputRef.current?.focus(), 0);
  };

  const specialties = parseSpecialties(profile.specialties);
  const displayLogoUrl = profile.logoUrl || buildProfilePlaceholderDataUrl(profile.companyName || profile.name, 'company');
  const isDefaultCompanyLogo = displayLogoUrl === PRODUCER_PLACEHOLDER;
  const contactEmail = profile.contactEmail || profile.email || profile.user?.email || '';
  const phone = profile.phone || profile.contactPhone || profile.user?.phone || '';
  const website = profile.website || profile.siteUrl || '';
  const instagramHandle = profile.instagramHandle || profile.instagram || '';
  const contacts = [
    { type: 'email', val: contactEmail },
    { type: 'phone', val: phone },
    { type: 'web', val: website },
    { type: 'sns', val: instagramHandle },
  ].filter((c) => c.val);

  const metaItems = [
    profile.foundedYear && { icon: 'calendar', label: `설립 ${profile.foundedYear}년` },
    profile.address && { icon: 'location', label: profile.address },
    profile.employeeCount && { icon: 'people', label: profile.employeeCount },
  ].filter(Boolean);

  return (
    <div className={styles.overviewSection}>
      {isEdit ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
            <button onClick={handleCancel} className={styles.cancelBtn}>취소</button>
            <button onClick={handleSave} disabled={saving} className={styles.saveBtn}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path d="M17 21v-8H7v8M7 3v5h8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>

          <div className={styles.editCard}>
            <span className={styles.editCardTitle}>기본 정보</span>

            <div className={styles.infoInput}>
  <span className={styles.infoInputLabel}>회사 로고</span>
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
    <div
      style={{
        width: '250px',
        height: '250px',
        borderRadius: '20px',
        background: '#F7F7FA',
        border: '1.5px dashed #C8C8D0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative', // 자식인 input이 이 영역을 기준으로 꽉 차게 함
        flexShrink: 0,
      }}
    >
      {/* 1. 실제 파일 인풋: 박스 전체를 덮고 투명하게 만듦 */}
      <input
        ref={logoInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={handleLogoChange}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0, // 눈에 안 보이게 함
          cursor: 'pointer',
          zIndex: 5, // 안내 문구보다 위에 와야 클릭이 됨
        }}
      />

      {/* 2. 이미지가 있을 때 보여줄 화면 */}
      {editData.logoUrl ? (
        <>
          <img
            src={editData.logoUrl}
            alt="로고"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {/* 삭제 버튼은 인풋보다 위에 있어야 하므로 zIndex를 더 높게 설정 */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteLogo(e);
            }}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10, 
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </>
      ) : (
        /* 3. 이미지가 없을 때 보여줄 안내 문구 (인풋 아래에 깔림) */
        <>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 4v12M12 16l-4-4M12 16l4-4"
              stroke="#A2A2A2"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M4 20h16" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: '13px', color: '#A2A2A2', mt: '10px', textAlign: 'center' }}>
            로고 이미지를 첨부하세요.
            <br />
            jpg, png, webp, svg (최대 10mb)
          </span>
        </>
      )}
    </div>
  </div>
</div>

            <div className={styles.infoInput}>
              <span className={styles.infoInputLabel}>제작사명</span>
              <input
                value={editData.companyName}
                onChange={set('companyName')}
                placeholder="입력하세요."
                className={styles.infoInputField}
              />
            </div>

            <div className={styles.infoInput}>
              <span className={styles.infoInputLabel}>소개</span>
              <textarea
                value={editData.description}
                onChange={set('description')}
                placeholder="회사 소개를 입력하세요."
                className={styles.infoInputField}
                rows={5}
              />
            </div>

            <div className={styles.editGrid3}>
              <div className={styles.infoInput}>
                <span className={styles.infoInputLabel}>설립년도</span>
                <select value={editData.foundedYear} onChange={set('foundedYear')} className={styles.infoInputField}>
                  <option value="">선택</option>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}년</option>
                  ))}
                </select>
              </div>

              <div className={styles.infoInput}>
                <span className={styles.infoInputLabel}>위치</span>
                <input
                  value={editData.address}
                  onChange={set('address')}
                  placeholder="예: 서울 강남구"
                  className={styles.infoInputField}
                />
              </div>

              <div className={styles.infoInput}>
                <span className={styles.infoInputLabel}>직원수</span>
                <input
                  value={editData.employeeCount}
                  onChange={set('employeeCount')}
                  placeholder="예: 50-100명"
                  className={styles.infoInputField}
                />
              </div>
            </div>
          </div>

          <div className={styles.editCardRow}>
            <div className={styles.editCardHalf}>
              <span className={styles.editCardTitle}>전문 분야</span>
              <div className={styles.editTagsRow}>
                {editData.specialties.map((tag) => (
                  <span key={tag} className={styles.editTag}>
                    {tag}
                    <button onClick={() => removeTag(tag)} className={styles.editTagRemoveBtn}>×</button>
                  </span>
                ))}
                {showTagInput ? (
                  <input
                    ref={tagInputRef}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKey}
                    onBlur={handleTagBlur}
                    placeholder="분야 입력"
                    className={styles.tagInput}
                  />
                ) : (
                  <button onClick={handlePlusClick} className={styles.addTagBtn}>
                    <span className={styles.addTagBtnIcon}>+</span>
                  </button>
                )}
              </div>
            </div>

            <div className={styles.editCardHalf}>
              <span className={styles.editCardTitle}>Contact</span>
              {[
                { key: 'website', label: '웹사이트', ph: 'https://...' },
                { key: 'contactEmail', label: '이메일', ph: 'contact@company.com' },
                { key: 'phone', label: '전화번호', ph: '02-1234-5678', readOnly: true },
                { key: 'instagramHandle', label: '인스타그램', ph: '@handle' },
              ].map(({ key, label, ph, readOnly }) => (
                <div key={key} className={styles.infoInput}>
                  <span className={styles.infoInputLabel}>{label}</span>
                  <input
                    value={editData[key]}
                    onChange={readOnly ? undefined : set(key)}
                    placeholder={ph}
                    readOnly={readOnly}
                    className={styles.infoInputField}
                    style={readOnly ? { background: '#F5F5F7', color: '#A2A2A2', cursor: 'not-allowed' } : undefined}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.card}>
            <div className={styles.companyHeader}>
              <div 
                className={styles.companyLogo} 
                onClick={handleLogoClickFromReadOnly}
                style={{ cursor: 'pointer' }}
                title="클릭하여 로고 변경"
              >
                <img
                  src={displayLogoUrl}
                  alt="logo"
                  className={styles.companyLogoImg}
                  loading="lazy"
                  width={60}
                  height={60}
                  style={isDefaultCompanyLogo ? { width: '34px', height: '34px', objectFit: 'contain' } : undefined}
                />
              </div>

              <div className={styles.companyInfo} style={{ flex: 1 }}>
                <div className={styles.companyNameRow}>
                  <span className={styles.companyName}>{profile.companyName || '—'}</span>
                  <span className={styles.verifiedBadge}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 6L9 17l-5-5"
                        stroke="#B45309"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    인증됨
                  </span>
                </div>
                {profile.description && <p className={styles.companyDesc}>{profile.description}</p>}
              </div>

              <button onClick={handleEnterEdit} className={styles.editBtn} style={{ alignSelf: 'flex-start' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                    stroke="#090909"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                    stroke="#090909"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                편집
              </button>
            </div>

            {metaItems.length > 0 && (
              <div className={styles.companyMeta}>
                {metaItems.map(({ icon, label }) => (
                  <MetaItem key={label} icon={icon} label={label} />
                ))}
              </div>
            )}
          </div>

          <div className={styles.cardRow}>
            <div className={styles.specialtiesCard}>
              <span className={styles.cardSectionLabel}>전문분야</span>
              {specialties.length > 0 ? (
                <div className={styles.genreTags}>
                  {specialties.map((s) => <GenreTag key={s} label={s} yellow />)}
                </div>
              ) : (
                <span className={styles.emptyHint}>편집을 눌러 전문 분야를 추가하세요.</span>
              )}
            </div>

            <div className={styles.contactCard}>
              <span className={styles.cardSectionLabel}>Contact</span>
              {contacts.length > 0 ? (
                <div className={styles.contactList}>
                  {contacts.map(({ type, val }) => (
                    <div key={type} className={styles.contactItem}>
                      <ContactIcon type={type} />
                      <span className={styles.contactValue}>{val}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className={styles.emptyHint}>편집을 눌러 연락처를 추가하세요.</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function WorkModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(
    initial
      ? {
          title: initial.title || '',
          genre: initial.genre || GENRES[0],
          year: String(initial.year ?? initial.releaseYear ?? ''),
          platform: initial.platform ?? '',
          description: initial.description ?? '',
        }
      : { title: '', genre: GENRES[0], year: '', platform: '', description: '' }
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initial?.imageUrl ?? null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.title || uploading) return;
    setUploading(true);
    try {
      let imageFileId = initial?.imageFileId ?? null;
      let imageUrl = initial?.imageUrl ?? null;
      if (imageFile) {
        const result = await uploadFile(imageFile, 'IMAGE');
        imageFileId = result.fileEntityId;
        imageUrl = result.url;
      }
      onSave({ ...form, imageFileId, imageUrl });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.workModalBox} onClick={(e) => e.stopPropagation()}>
        <div className={styles.workModalHeader}>
          <span className={styles.workModalTitle}>{initial ? '작품 수정' : '작품 추가'}</span>
          <button onClick={onClose} className={styles.workModalCloseBtn}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#888896" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div
          className={`${styles.imageUploadArea} ${imagePreview ? styles.imageUploadAreaFilled : ''}`}
          onClick={() => fileInputRef.current?.click()}
        >
          {imagePreview ? (
            <>
              <img src={imagePreview} alt="preview" className={styles.imageUploadPreview} />
              <button
                className={styles.imageUploadChangeBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                변경
              </button>
            </>
          ) : (
            <>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="#C8C8D0" strokeWidth="1.5" />
                <circle cx="8.5" cy="8.5" r="1.5" stroke="#C8C8D0" strokeWidth="1.5" />
                <path
                  d="M21 15l-5-5L5 21"
                  stroke="#C8C8D0"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className={styles.imageUploadLabel}>클릭하여 썸네일 업로드</span>
              <span className={styles.imageUploadSub}>JPG, PNG, WEBP 지원</span>
            </>
          )}
        </div>

        <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />

        <div className={styles.workModalFields}>
          <div className={styles.workModalField}>
            <span className={styles.workModalLabel}>
              작품명 <span className={styles.required}>*</span>
            </span>
            <input
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="작품명을 입력하세요"
              className={styles.workModalInput}
            />
          </div>

          <div className={styles.workModalField}>
            <span className={styles.workModalLabel}>장르</span>
            <select value={form.genre} onChange={(e) => update('genre', e.target.value)} className={styles.workModalInput}>
              {GENRES.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div className={styles.workModalFieldRow}>
            <div className={styles.workModalField}>
              <span className={styles.workModalLabel}>제작 연도</span>
              <input
                value={form.year}
                onChange={(e) => update('year', e.target.value)}
                placeholder="예: 2024"
                className={styles.workModalInput}
              />
            </div>
            <div className={styles.workModalField}>
              <span className={styles.workModalLabel}>플랫폼</span>
              <input
                value={form.platform}
                onChange={(e) => update('platform', e.target.value)}
                placeholder="예: 넷플릭스"
                className={styles.workModalInput}
              />
            </div>
          </div>

          <div className={styles.workModalField}>
            <span className={styles.workModalLabel}>작품 소개 (선택)</span>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="작품에 대한 간단한 소개를 입력하세요."
              className={`${styles.workModalInput} ${styles.workModalTextarea}`}
              rows={3}
            />
          </div>
        </div>

        <div className={styles.workModalFooter}>
          <button onClick={onClose} className={styles.workModalCancelBtn}>취소</button>
          <button
            onClick={handleSave}
            disabled={!form.title || uploading}
            className={styles.workModalSaveBtn}
            style={{
              background: form.title && !uploading ? '#090909' : '#A9A9B2',
              cursor: form.title && !uploading ? 'pointer' : 'default',
            }}
          >
            {uploading ? '업로드 중...' : initial ? '수정 완료' : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ work, onEdit, onDelete }) {
  return (
    <div className={styles.workCard}>
      <div className={styles.workThumbnail}>
        {work.imageUrl ? (
          <img src={work.imageUrl} alt={work.title} className={styles.workThumbnailImg} loading="lazy" width={300} height={168} />
        ) : (
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#C8C8D0" strokeWidth="1.5" />
            <path d="M3 9h18M9 21V9" stroke="#C8C8D0" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        )}
      </div>

      <div className={styles.workInfo}>
        <div className={styles.workMeta}>
          {work.genre && <GenreTag label={work.genre} yellow />}
          {(work.year || work.releaseYear) && (
            <span className={styles.workYear}>{work.year ?? work.releaseYear}</span>
          )}
        </div>
        <span className={styles.workTitle}>{work.title}</span>
        {work.platform && <span className={styles.workPlatform}>{work.platform}</span>}
        {work.description && <p className={styles.workDesc}>{work.description}</p>}
      </div>

      <div className={styles.workActions}>
        <button className={styles.workEditBtn} onClick={() => onEdit(work)}>수정</button>
        <button className={styles.workDeleteBtn} onClick={() => onDelete(work.id)}>삭제</button>
      </div>
    </div>
  );
}

function CompanyProjectsSection({ works, onAdd, onEdit, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const handleSave = (form) => {
    if (editTarget) {
      onEdit(editTarget.id, form);
      setEditTarget(null);
    } else {
      onAdd(form);
      setShowModal(false);
    }
  };

  return (
    <div>
      <div className={styles.worksHeader}>
        <span className={styles.worksTitle}>제작 작품 ({works.length})</span>
        <button className={styles.addWorkBtn} onClick={() => setShowModal(true)}>+ 작품 추가</button>
      </div>

      {works.length === 0 ? (
        <div className={styles.emptyState}>등록된 작품이 없습니다.</div>
      ) : (
        <div className={styles.worksGrid}>
          {works.map((w) => (
            <ProjectCard key={w.id} work={w} onEdit={setEditTarget} onDelete={onDelete} />
          ))}
        </div>
      )}

      {showModal && !editTarget && <WorkModal onClose={() => setShowModal(false)} onSave={handleSave} />}
      {editTarget && <WorkModal initial={editTarget} onClose={() => setEditTarget(null)} onSave={handleSave} />}
    </div>
  );
}

function PostingsTab({ postings }) {
  if (postings.length === 0) return <div className={styles.emptyState}>진행 중인 공고가 없습니다.</div>;

  return (
    <div className={styles.postingsGrid}>
      {postings.map((p) => (
        <div key={p.id} className={styles.postingCard}>
          <div className={styles.postingTags}>
            <GenreTag label={p.category ?? p.projectType} yellow />
            {p.regionProvince && <span className={styles.locationTag}>{p.regionProvince}</span>}
            <span className={styles.ddayTag}>{dday(p.applicationDeadline)}</span>
          </div>

          <div className={styles.postingTitle}>{p.title}</div>

          <div className={styles.postingMeta}>
            <div className={styles.postingMetaItem}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="#A2A2A2" strokeWidth="1.5" />
                <path d="M16 2v4M8 2v4M3 10h18" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span className={styles.postingMetaText}>마감 {p.applicationDeadline}</span>
            </div>

            {p.applicantCount != null && (
              <div className={styles.postingMetaItem}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="4" stroke="#A2A2A2" strokeWidth="1.5" />
                  <path d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span className={styles.postingMetaText}>지원자 {p.applicantCount}명</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Reviews Tab ── */
function ReviewsTab() {
  return <ComingSoonBox />;
}

export default function ProducerCompanyPage() {
  const { setUserInfo } = useAuth();
  const [tab, setTab] = useState('회사 소개');
  const [profile, setProfile] = useState({});
  const [works, setWorks] = useState([]);
  const [postings, setPostings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [producerStats, setProducerStats] = useState(null);

  useEffect(() => {
    getMyProducerProfile().then(p => {
      // 로고 삭제 플래그가 있으면 서버 logoUrl 무시
      const logoRemoved = isProducerLogoRemoved();
      const profileWithResolvedLogo = logoRemoved ? { ...p, logoUrl: null } : p;
      setProfile(profileWithResolvedLogo);
      syncProducerIdentity(setUserInfo, profileWithResolvedLogo);
      persistProducerIdentity(profileWithResolvedLogo);
    }).catch(() => {});
    getProducerWorks().then(setWorks).catch(() => {});
    getProducerReviews().then(setReviews).catch(() => {});
    getProducerStats().then(setProducerStats).catch(() => {});
  }, [setUserInfo]);

  useEffect(() => {
    if (tab === '진행 중인 공고') {
      getMyCastingList({ status: 'OPEN' })
        .then((data) => {
          setPostings(Array.isArray(data) ? data : data?.content ?? []);
        })
        .catch(() => {});
    }
  }, [tab]);

  const handleAddWork = (form) => {
    createProducerWork(form)
      .then((created) => setWorks((prev) => [...prev, { ...form, id: created.id }]))
      .catch(() => {});
  };

  const handleEditWork = (id, form) => {
    updateProducerWork(id, form)
      .then(() => setWorks((prev) => prev.map((w) => (w.id === id ? { ...w, ...form } : w))))
      .catch(() => {});
  };

  const handleDeleteWork = (id) => {
    deleteProducerWork(id)
      .then(() => setWorks((prev) => prev.filter((w) => w.id !== id)))
      .catch(() => {});
  };

  const stats = [
    {
      label: '총 조회수',
      value: producerStats ? (producerStats.totalCastingViews ?? 0).toLocaleString() : '—',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="#10B981" strokeWidth="1.8"/></svg>,
    },
    {
      label: '주간 조회자',
      value: producerStats ? (producerStats.weeklyViewers ?? 0).toLocaleString() : '—',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="#10B981" strokeWidth="1.8"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
    {
      label: '북마크 수',
      value: producerStats ? (producerStats.bookmarkCount ?? 0).toLocaleString() : '—',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
    {
      label: '진행 중인 공고 수',
      value: producerStats ? (producerStats.activePostingCount ?? 0).toLocaleString() : '—',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
  ];

  return (
    <ProducerContentContainer>
      <div style={{ padding: '50px 40px 40px' }}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.pageTitle}>제작사 정보</h1>
            <span className={styles.pageSubtitle}>제작사의 프로필과 작품을 관리하고 배우들에게 노출하세요.</span>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 40px 60px' }}>
        <div className={styles.statsCard}>
          {stats.map(({ label, value, icon }) => (
            <div key={label} className={styles.statItem}>
              <span className={styles.statValue}>{value}</span>
              <div className={styles.statMeta}>
                {icon}
                <span className={styles.statLabel}>{label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.tabs}>
          {TABS.map((tb) => (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              className={`${styles.tabBtn} ${tab === tb ? styles.tabBtnActive : ''}`}
            >
              {tb}
            </button>
          ))}
        </div>

        {tab === '회사 소개' && (
          <CompanyOverview profile={profile} onProfileUpdate={setProfile} />
        )}

        {tab === '제작 작품' && (
          <CompanyProjectsSection
            works={works}
            onAdd={handleAddWork}
            onEdit={handleEditWork}
            onDelete={handleDeleteWork}
          />
        )}

        {tab === '진행 중인 공고' && <PostingsTab postings={postings} />}
        {tab === '리뷰' && <ReviewsTab reviews={reviews} />}
      </div>
    </ProducerContentContainer>
  );
}
